import AndroidIcon from "@mui/icons-material/Android";
import AppleIcon from "@mui/icons-material/Apple";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import PendingIcon from "@mui/icons-material/Pending";
import RateReviewIcon from "@mui/icons-material/RateReview";
import { Box, Stack, Typography } from "@mui/material";
import InternCard from "../components/InternCard";
import InternStatusBadge from "../components/InternStatusBadge";

interface ReviewData {
  id: string;
  platform: string;
  appVersion: string;
  buildNumber: string;
  status: string;
  statusDisplay: string;
  submittedAt: string | null;
  reviewedAt: string | null;
}

interface Props {
  iosReview: ReviewData | null;
  androidReview: ReviewData | null;
  /** Render content only — parent provides the card chrome */
  embedded?: boolean;
}

const getStatusConfig = (status: string) => {
  switch (status) {
    case "ready_for_sale":
    case "approved":
      return {
        status: "success" as const,
        icon: CheckCircleIcon,
        label: "Approved",
      };
    case "in_review":
      return {
        status: "info" as const,
        icon: RateReviewIcon,
        label: "In Review",
      };
    case "waiting_for_review":
      return {
        status: "warning" as const,
        icon: HourglassEmptyIcon,
        label: "Waiting",
      };
    case "pending_developer_release":
      return {
        status: "info" as const,
        icon: PendingIcon,
        label: "Pending Release",
      };
    case "rejected":
      return {
        status: "error" as const,
        icon: ErrorIcon,
        label: "Rejected",
      };
    default:
      return {
        status: "default" as const,
        icon: PendingIcon,
        label: status || "Unknown",
      };
  }
};

const formatDate = (dateString: string | null): string => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

interface PlatformReviewProps {
  platform: "ios" | "android";
  review: ReviewData | null;
}

const PlatformReview = ({ platform, review }: PlatformReviewProps) => {
  const isIOS = platform === "ios";
  const Icon = isIOS ? AppleIcon : AndroidIcon;
  const platformLabel = isIOS ? "iOS" : "Android";

  if (!review) {
    return (
      <Box
        sx={{
          flex: 1,
          p: 2,
          borderRadius: 1.5,
          bgcolor: "var(--intern-bg-secondary)",
          border: "1px solid var(--intern-border-default)",
          minWidth: 140,
          height: "100%",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
          <Icon
            sx={{
              fontSize: 18,
              color: isIOS ? "#007AFF" : "#3DDC84",
            }}
          />
          <Typography
            variant="subtitle2"
            sx={{
              color: "var(--intern-text-primary)",
              fontWeight: 600,
              fontSize: "0.8125rem",
            }}
          >
            {platformLabel}
          </Typography>
        </Stack>
        <Typography
          variant="body2"
          sx={{
            color: "var(--intern-text-tertiary)",
            fontSize: "0.75rem",
            fontStyle: "italic",
          }}
        >
          No recent submissions
        </Typography>
      </Box>
    );
  }

  const statusConfig = getStatusConfig(review.status);

  return (
    <Box
      sx={{
        flex: 1,
        p: 2,
        borderRadius: 1.5,
        bgcolor: "var(--intern-bg-secondary)",
        border: "1px solid var(--intern-border-default)",
        minWidth: 140,
        height: "100%",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={1.5}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Icon
            sx={{
              fontSize: 18,
              color: isIOS ? "#007AFF" : "#3DDC84",
            }}
          />
          <Typography
            variant="subtitle2"
            sx={{
              color: "var(--intern-text-primary)",
              fontWeight: 600,
              fontSize: "0.8125rem",
            }}
          >
            {platformLabel}
          </Typography>
        </Stack>
        <InternStatusBadge
          status={statusConfig.status}
          label={statusConfig.label}
          size="small"
        />
      </Stack>

      <Stack spacing={0.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography
            variant="caption"
            sx={{
              color: "var(--intern-text-tertiary)",
              fontSize: "0.6875rem",
              minWidth: 60,
            }}
          >
            Version
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "var(--intern-text-primary)",
              fontWeight: 500,
              fontSize: "0.75rem",
            }}
          >
            v{review.appVersion} ({review.buildNumber})
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography
            variant="caption"
            sx={{
              color: "var(--intern-text-tertiary)",
              fontSize: "0.6875rem",
              minWidth: 60,
            }}
          >
            Submitted
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "var(--intern-text-secondary)",
              fontSize: "0.75rem",
            }}
          >
            {formatDate(review.submittedAt)}
          </Typography>
        </Stack>

        {review.reviewedAt && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              variant="caption"
              sx={{
                color: "var(--intern-text-tertiary)",
                fontSize: "0.6875rem",
                minWidth: 60,
              }}
            >
              Reviewed
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "var(--intern-text-secondary)",
                fontSize: "0.75rem",
              }}
            >
              {formatDate(review.reviewedAt)}
            </Typography>
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

const InternAppReviewCard = ({
  iosReview,
  androidReview,
  embedded = false,
}: Props) => {
  const content = (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.5}
      sx={embedded ? { height: "100%", alignItems: "stretch" } : undefined}
    >
      <PlatformReview platform="ios" review={iosReview} />
      <PlatformReview platform="android" review={androidReview} />
    </Stack>
  );

  if (embedded) return content;

  return (
    <InternCard title="App Review Status" variant="compact">
      {content}
    </InternCard>
  );
};

export default InternAppReviewCard;
